// =============================================================================
// OverridesTab — per-date layer (block / reduce capacity / reopen)
// =============================================================================
// Pick (location, room) — no experience filter. The week grid shows ALL
// experiences running at that room. Each cell renders a colored square per
// experience with its current state (open / experience-blocked). Click a
// square → toggle that experience's override. Click the cell-level ✕ →
// block the whole room (covers every experience). Capacity +/- adjusts the
// shared room capacity.
//
// Schema reminder (post-0040):
//   schedule_overrides.experience_id NULL     → room-wide override
//   schedule_overrides.experience_id NOT NULL → applies to that experience only
//   Resolution priority: room-wide > experience-specific > defaults
//
// Reason (schedule_overrides.reason): an optional short text typed above the
// grid ("privé-event", "onderhoud headsets", "teamuitje <bedrijf>") goes on
// every override set while it is filled in; the calendar shows it on the slot.
// An empty field keeps the reason an override already had.
// =============================================================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../lib/auth';
import { formatError } from '../../lib/errors';
import { fetchWalkinRefData, type BookingExperience, type BookingLocation } from '../../lib/walkin-data';
import { fetchDepartures, type Departure } from '../../lib/departures';
import {
  listRooms,
  listScheduleOverrides,
  setScheduleOverride,
  type AdminRoomRow,
  type OverrideRow,
} from '../../lib/setup';
import { useLocationFilter } from '../../lib/location-context';
import {
  todayVenue,
  venueWeekStart,
  rangeDays,
  addDaysVenue,
  formatVenueShort,
  venueWeekLabel,
} from '../../lib/dates';

// Palette ordered for maximum visual distance between adjacent indices, so
// any two experiences (assigned by sorted-position-in-legend) read as clearly
// different colors. Hash-based assignment was making Egypt+Odyssey both land
// on purplish entries.
// Quick picks for the reason field (Rick, 2026-10-23).
const REASON_PRESETS = ['Privé-event', 'Onderhoud headsets', 'Schoolgroep', 'Teamuitje'];

const PALETTE = [
  { bg: '#e0eaff', fg: '#1d4ed8' },  // 0 blue
  { bg: '#fed7aa', fg: '#9a3412' },  // 1 orange      (max distance from blue)
  { bg: '#e3f4e8', fg: '#1f7a3d' },  // 2 green
  { bg: '#fce7f3', fg: '#9d174d' },  // 3 pink
  { bg: '#cffafe', fg: '#0e7490' },  // 4 cyan
  { bg: '#fde2e2', fg: '#a40e0e' },  // 5 red
  { bg: '#fef3c7', fg: '#8a6300' },  // 6 amber
  { bg: '#ede9fe', fg: '#5b21b6' },  // 7 violet
];

interface CellState {
  slot_start: string;
  date: string;
  time_of_day: string;
  room_id: string;
  capacity: number;        // shared room capacity for this slot (after room-wide override)
  totalBooked: number;     // sum across experiences
  experiences: Array<{
    experience_id: string;
    experience_name: string;
    booked: number;
    blocked: boolean;       // experience-specific block in effect
  }>;
  roomBlocked: boolean;     // room-wide block in effect
  reducedToCapacity: number | null; // if room-wide reduced_capacity override
  roomReason: string | null;        // reason on the room-wide override
  reasons: string[];                // every distinct reason in this slot
}

export function OverridesTab() {
  const { operator } = useAuth();
  const { locationId: globalLocationId } = useLocationFilter();

  const [locations, setLocations] = useState<BookingLocation[]>([]);
  const [experiences, setExperiences] = useState<BookingExperience[]>([]);
  const [rooms, setRooms] = useState<AdminRoomRow[]>([]);
  const [refLoading, setRefLoading] = useState(true);
  const [refError, setRefError] = useState<string | null>(null);

  const [locationId, setLocationId] = useState<string>('');
  const [roomId, setRoomId] = useState<string>('');

  const [anchor, setAnchor] = useState<string>(() => todayVenue());
  const [departures, setDepartures] = useState<Departure[]>([]);
  const [overrides, setOverrides] = useState<OverrideRow[]>([]);
  const [gridLoading, setGridLoading] = useState(false);
  const [gridError, setGridError] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const days = useMemo(() => rangeDays(venueWeekStart(anchor), 7), [anchor]);
  const fromDate = days[0]!;
  const toDate = days[days.length - 1]!;

  // ---- Refdata load ------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRefLoading(true);
      setRefError(null);
      try {
        const [ref, roomList] = await Promise.all([
          fetchWalkinRefData(true),
          listRooms(),
        ]);
        if (cancelled) return;
        setLocations(ref.locations);
        setExperiences(ref.experiences);
        setRooms(roomList);
        setLocationId(globalLocationId ?? ref.locations[0]?.id ?? '');
      } catch (e) {
        if (!cancelled) setRefError(formatError(e));
      } finally {
        if (!cancelled) setRefLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const roomsAtLocation = useMemo(
    () => rooms.filter((r) => r.location_id === locationId),
    [rooms, locationId],
  );
  useEffect(() => {
    if (roomsAtLocation.length === 0) {
      setRoomId('');
    } else if (!roomsAtLocation.some((r) => r.id === roomId)) {
      setRoomId(roomsAtLocation[0]!.id);
    }
  }, [roomsAtLocation, roomId]);

  // ---- Grid load ---------------------------------------------------------
  const reloadGrid = useCallback(async () => {
    if (!roomId) {
      setDepartures([]);
      setOverrides([]);
      return;
    }
    setGridLoading(true);
    setGridError(null);
    try {
      const fromIso = new Date(`${fromDate}T00:00:00`).toISOString();
      const toIso = new Date(`${addDaysVenue(toDate, 1)}T00:00:00`).toISOString();
      const [deps, overs] = await Promise.all([
        // No experience filter — fetch everything at the location for the week.
        fetchDepartures(fromDate, toDate, { locationId }),
        listScheduleOverrides(roomId, fromIso, toIso),
      ]);
      setDepartures(deps.filter((d) => d.room_id === roomId));
      setOverrides(overs);
    } catch (e) {
      setGridError(formatError(e));
    } finally {
      setGridLoading(false);
    }
  }, [roomId, locationId, fromDate, toDate]);
  useEffect(() => {
    void reloadGrid();
  }, [reloadGrid]);

  // ---- Override actions --------------------------------------------------
  const handleToggleExperience = useCallback(
    async (cell: CellState, experienceId: string, currentlyBlocked: boolean) => {
      if (!operator?.id) return;
      try {
        await setScheduleOverride(
          operator.id,
          cell.room_id,
          cell.slot_start,
          currentlyBlocked ? null : 'blocked',  // toggle
          null,
          reasonOrNull(reason),
          experienceId,
        );
        void reloadGrid();
      } catch (e) {
        setGridError(formatError(e));
      }
    },
    [operator?.id, reloadGrid, reason],
  );

  const handleAdjustCapacity = useCallback(
    async (cell: CellState, deltaSign: 1 | -1) => {
      if (!operator?.id) return;
      const baseCapacity =
        cell.reducedToCapacity != null ? cell.reducedToCapacity : cell.capacity;
      const newCap = Math.max(0, baseCapacity + deltaSign);
      try {
        await setScheduleOverride(
          operator.id,
          cell.room_id,
          cell.slot_start,
          'reduced_capacity',
          newCap,
          reasonOrNull(reason) ?? cell.roomReason,
          null,                                // room-wide
        );
        void reloadGrid();
      } catch (e) {
        setGridError(formatError(e));
      }
    },
    [operator?.id, reloadGrid, reason],
  );

  const handleCloseRoom = useCallback(
    async (cell: CellState) => {
      if (!operator?.id) return;
      try {
        await setScheduleOverride(
          operator.id,
          cell.room_id,
          cell.slot_start,
          'blocked',
          null,
          reasonOrNull(reason) ?? cell.roomReason,
          null,                                 // room-wide
        );
        void reloadGrid();
      } catch (e) {
        setGridError(formatError(e));
      }
    },
    [operator?.id, reloadGrid, reason],
  );

  const handleReopenAll = useCallback(
    async (cell: CellState) => {
      if (!operator?.id) return;
      try {
        // Clear room-wide first
        await setScheduleOverride(
          operator.id,
          cell.room_id,
          cell.slot_start,
          null,
          null,
          null,
          null,
        );
        // Clear every experience-specific override at this slot
        for (const exp of cell.experiences) {
          if (exp.blocked) {
            await setScheduleOverride(
              operator.id,
              cell.room_id,
              cell.slot_start,
              null,
              null,
              null,
              exp.experience_id,
            );
          }
        }
        void reloadGrid();
      } catch (e) {
        setGridError(formatError(e));
      }
    },
    [operator?.id, reloadGrid],
  );

  // ---- Build the cell grid ----------------------------------------------
  // grid: time_of_day → date → CellState
  const grid = useMemo<Array<[string, Map<string, CellState>]>>(() => {
    // First, group departures by (slot_start, room) — multiple experiences
    // at the same (room, slot) collapse into one cell.
    const cellsBySlot = new Map<string, CellState>();
    for (const d of departures) {
      let cell = cellsBySlot.get(d.slot_start);
      if (!cell) {
        cell = {
          slot_start: d.slot_start,
          date: d.date,
          time_of_day: d.time_of_day,
          room_id: d.room_id,
          capacity: d.capacity,
          totalBooked: 0,
          experiences: [],
          roomBlocked: false,
          reducedToCapacity: null,
          roomReason: null,
          reasons: [],
        };
        cellsBySlot.set(d.slot_start, cell);
      }
      cell.experiences.push({
        experience_id: d.experience_id,
        experience_name: d.experience_name,
        booked: d.booked_in_experience,
        blocked: false,    // filled in below from overrides
      });
      // d.booked is room-wide and identical across the experience rows for
      // this (room, slot); set it once rather than accumulate.
      cell.totalBooked = d.booked;
      cell.capacity = d.capacity; // same value across experiences post-0036
    }

    // Apply overrides on top.
    for (const o of overrides) {
      const cell = cellsBySlot.get(o.slot_start);
      if (!cell) continue;
      if (o.experience_id == null) {
        // Room-wide
        if (o.kind === 'blocked') cell.roomBlocked = true;
        else if (o.kind === 'reduced_capacity') {
          cell.reducedToCapacity = o.override_capacity ?? null;
        }
        cell.roomReason = o.reason?.trim() || null;
      } else {
        const exp = cell.experiences.find((e) => e.experience_id === o.experience_id);
        if (exp && o.kind === 'blocked') exp.blocked = true;
      }
      const r = o.reason?.trim();
      if (r && !cell.reasons.includes(r)) cell.reasons.push(r);
    }

    // Project to grid: time_of_day → date → cell
    const byTime = new Map<string, Map<string, CellState>>();
    for (const cell of cellsBySlot.values()) {
      let row = byTime.get(cell.time_of_day);
      if (!row) {
        row = new Map();
        byTime.set(cell.time_of_day, row);
      }
      row.set(cell.date, cell);
    }
    return [...byTime.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [departures, overrides]);

  // Per-day bulk: close every slot on that date with a room-wide block, or
  // reopen all room-wide blocks (experience-specific blocks at the slot
  // level are not touched — operator can drill in if needed).
  const cellsForDate = useCallback(
    (date: string): CellState[] => {
      const out: CellState[] = [];
      for (const [, byDate] of grid) {
        const c = byDate.get(date);
        if (c) out.push(c);
      }
      return out;
    },
    [grid],
  );

  const handleCloseDay = useCallback(
    async (date: string) => {
      if (!operator?.id) return;
      const cells = cellsForDate(date).filter((c) => !c.roomBlocked);
      if (cells.length === 0) return;
      const why = reasonOrNull(reason);
      if (!window.confirm(
        `Sluit alle ${cells.length} tijdsloten op deze dag?` + (why ? `\nReden: ${why}` : ''),
      )) return;
      try {
        for (const cell of cells) {
          await setScheduleOverride(
            operator.id, cell.room_id, cell.slot_start,
            'blocked', null, why ?? cell.roomReason, null,
          );
        }
        void reloadGrid();
      } catch (e) {
        setGridError(formatError(e));
      }
    },
    [operator?.id, cellsForDate, reloadGrid, reason],
  );

  // Put the typed reason on an existing room-wide override (closed slot or
  // changed capacity) without changing what it does.
  const handleApplyReason = useCallback(
    async (cell: CellState) => {
      if (!operator?.id) return;
      const why = reasonOrNull(reason);
      if (!why) return;
      try {
        if (cell.roomBlocked) {
          await setScheduleOverride(operator.id, cell.room_id, cell.slot_start, 'blocked', null, why, null);
        } else if (cell.reducedToCapacity != null) {
          await setScheduleOverride(
            operator.id, cell.room_id, cell.slot_start,
            'reduced_capacity', cell.reducedToCapacity, why, null,
          );
        }
        void reloadGrid();
      } catch (e) {
        setGridError(formatError(e));
      }
    },
    [operator?.id, reloadGrid, reason],
  );

  const handleOpenDay = useCallback(
    async (date: string) => {
      if (!operator?.id) return;
      const cells = cellsForDate(date).filter((c) => c.roomBlocked);
      if (cells.length === 0) return;
      try {
        for (const cell of cells) {
          await setScheduleOverride(
            operator.id, cell.room_id, cell.slot_start,
            null, null, null, null,
          );
        }
        void reloadGrid();
      } catch (e) {
        setGridError(formatError(e));
      }
    },
    [operator?.id, cellsForDate, reloadGrid],
  );

  // Legend = unique experiences across the grid, sorted by name for stability.
  // Each one gets the next palette slot (max visual distance between adjacent
  // entries) so two-experience cells always look distinct.
  const legend = useMemo(() => {
    const seen = new Map<string, string>();
    for (const [, byDate] of grid) {
      for (const cell of byDate.values()) {
        for (const e of cell.experiences) {
          if (!seen.has(e.experience_id)) {
            seen.set(e.experience_id, e.experience_name);
          }
        }
      }
    }
    return [...seen.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [grid]);

  const colorByExpId = useMemo(() => {
    const m = new Map<string, { bg: string; fg: string }>();
    legend.forEach((e, i) => {
      m.set(e.id, PALETTE[i % PALETTE.length]!);
    });
    return m;
  }, [legend]);

  // ---- Render -----------------------------------------------------------
  if (refLoading) return <div className="muted">Laden…</div>;
  if (refError) return <div className="error-card">{refError}</div>;

  return (
    <div className="setup-overrides">
      <div className="walkin-row">
        <label className="walkin-field">
          <span>Locatie</span>
          <select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.display_name ?? l.name}
              </option>
            ))}
          </select>
        </label>
        {roomsAtLocation.length > 1 ? (
          <label className="walkin-field">
            <span>Ruimte</span>
            <select value={roomId} onChange={(e) => setRoomId(e.target.value)}>
              {roomsAtLocation.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      {legend.length > 0 ? (
        <div className="setup-overrides-legend">
          {legend.map((e) => {
            const pal = colorByExpId.get(e.id) ?? PALETTE[0]!;
            return (
              <span key={e.id} className="setup-overrides-legend-item">
                <span
                  className="setup-overrides-legend-square"
                  style={{ background: pal.bg, borderColor: pal.fg }}
                />
                <span>{e.name}</span>
              </span>
            );
          })}
        </div>
      ) : null}

      <div className="setup-overrides-toolbar">
        <button
          className="btn-link cal-today-btn"
          onClick={() => setAnchor(todayVenue())}
        >
          Vandaag
        </button>
        <div className="setup-overrides-week-nav">
          <button
            className="btn-link"
            onClick={() => setAnchor(addDaysVenue(anchor, -7))}
            aria-label="Vorige week"
          >
            ‹
          </button>
          <span className="cal-title">{venueWeekLabel(fromDate)}</span>
          <button
            className="btn-link"
            onClick={() => setAnchor(addDaysVenue(anchor, 7))}
            aria-label="Volgende week"
          >
            ›
          </button>
        </div>
        <div />
      </div>

      <div className="setup-overrides-reason">
        <label className="walkin-field">
          <span>Reden (optioneel)</span>
          <input
            type="text"
            value={reason}
            maxLength={80}
            placeholder="bv. privé-event, onderhoud headsets, schoolgroep"
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
        <div className="setup-overrides-reason-presets">
          {REASON_PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              className="setup-overrides-reason-chip"
              onClick={() => setReason(p === 'Teamuitje' ? 'Teamuitje ' : p)}
            >
              {p}
            </button>
          ))}
          {reason ? (
            <button type="button" className="btn-link" onClick={() => setReason('')}>
              Wissen
            </button>
          ) : null}
        </div>
        <div className="muted setup-overrides-reason-hint">
          Komt bij elk tijdslot dat je nu sluit, blokkeert of aanpast, en staat in de kalender.
        </div>
      </div>

      {gridError ? <div className="error-card">{gridError}</div> : null}
      {gridLoading ? <div className="muted">Laden…</div> : null}

      {!gridLoading && grid.length === 0 ? (
        <div className="muted setup-overrides-empty">
          Geen tijdsloten in deze week voor deze ruimte.
          Voeg ze toe via de Schedule-tab.
        </div>
      ) : null}

      {grid.length > 0 ? (
        <div
          className="cal-grid cal-grid-week setup-overrides-grid"
          style={{ gridTemplateColumns: `70px repeat(7, minmax(125px, 1fr))` }}
        >
          <div className="cal-corner" />
          {days.map((d) => {
            const cells = cellsForDate(d);
            const closedCells = cells.filter((c) => c.roomBlocked).length;
            const allClosed = cells.length > 0 && closedCells === cells.length;
            return (
              <div key={d} className="cal-day-head setup-overrides-day-head">
                <span className="setup-overrides-day-label">{formatVenueShort(d)}</span>
                {cells.length > 0 ? (
                  allClosed ? (
                    <button
                      className="setup-overrides-day-btn setup-overrides-day-open"
                      onClick={() => void handleOpenDay(d)}
                      title="Open hele dag (verwijdert alle slot-blokkades)"
                    >
                      Open dag
                    </button>
                  ) : (
                    <button
                      className="setup-overrides-day-btn setup-overrides-day-close"
                      onClick={() => void handleCloseDay(d)}
                      title="Sluit hele dag (blokkeert elk tijdslot)"
                    >
                      ✕ Sluit dag
                    </button>
                  )
                ) : null}
              </div>
            );
          })}

          {grid.map(([time, byDate]) => (
            <OverrideGridRow
              key={time}
              time={time}
              days={days}
              byDate={byDate}
              colorByExpId={colorByExpId}
              onToggleExperience={handleToggleExperience}
              onAdjustCapacity={handleAdjustCapacity}
              onCloseRoom={handleCloseRoom}
              onReopenAll={handleReopenAll}
              onApplyReason={reasonOrNull(reason) ? handleApplyReason : null}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function OverrideGridRow({
  time, days, byDate, colorByExpId,
  onToggleExperience, onAdjustCapacity, onCloseRoom, onReopenAll, onApplyReason,
}: {
  time: string;
  days: string[];
  byDate: Map<string, CellState>;
  colorByExpId: Map<string, { bg: string; fg: string }>;
  onToggleExperience: (cell: CellState, experienceId: string, currentlyBlocked: boolean) => void;
  onAdjustCapacity: (cell: CellState, sign: 1 | -1) => void;
  onCloseRoom: (cell: CellState) => void;
  onReopenAll: (cell: CellState) => void;
  onApplyReason: ((cell: CellState) => void) | null;
}) {
  return (
    <>
      <div className="cal-time-head">{time}</div>
      {days.map((day) => {
        const cell = byDate.get(day);
        if (!cell) {
          return <div key={day} className="cal-cell cal-cell-empty" />;
        }
        return (
          <OverrideCell
            key={day}
            cell={cell}
            colorByExpId={colorByExpId}
            onToggleExperience={onToggleExperience}
            onAdjustCapacity={onAdjustCapacity}
            onCloseRoom={onCloseRoom}
            onReopenAll={onReopenAll}
            onApplyReason={onApplyReason}
          />
        );
      })}
    </>
  );
}

function OverrideCell({
  cell, colorByExpId,
  onToggleExperience, onAdjustCapacity, onCloseRoom, onReopenAll, onApplyReason,
}: {
  cell: CellState;
  colorByExpId: Map<string, { bg: string; fg: string }>;
  onToggleExperience: (cell: CellState, experienceId: string, currentlyBlocked: boolean) => void;
  onAdjustCapacity: (cell: CellState, sign: 1 | -1) => void;
  onCloseRoom: (cell: CellState) => void;
  onReopenAll: (cell: CellState) => void;
  onApplyReason: ((cell: CellState) => void) | null;
}) {
  const reasonLine = cell.reasons.length > 0 ? (
    <div className="setup-override-reason" title={cell.reasons.join(' · ')}>
      {cell.reasons.join(' · ')}
    </div>
  ) : null;
  const applyReason = onApplyReason && (cell.roomBlocked || cell.reducedToCapacity != null) ? (
    <button className="btn-link setup-override-apply-reason" onClick={() => onApplyReason(cell)}>
      Reden toepassen
    </button>
  ) : null;

  if (cell.roomBlocked) {
    return (
      <div className="cal-cell setup-override-cell setup-override-closed">
        <div className="setup-override-closed-body">
          <div className="setup-override-closed-label">✕ Gesloten</div>
          {reasonLine}
          <button className="btn-link" onClick={() => onReopenAll(cell)}>
            Open opnieuw
          </button>
          {applyReason}
        </div>
      </div>
    );
  }

  const effectiveCapacity =
    cell.reducedToCapacity != null ? cell.reducedToCapacity : cell.capacity;
  const anyExperienceBlocked = cell.experiences.some((e) => e.blocked);

  return (
    <div className="cal-cell setup-override-cell">
      <div className="setup-override-cell-body">
        <div className="setup-override-exps">
          {cell.experiences.map((e) => {
            const pal = colorByExpId.get(e.experience_id) ?? PALETTE[0]!;
            return (
              <button
                key={e.experience_id}
                className={`setup-override-exp ${e.blocked ? 'setup-override-exp-blocked' : ''}`}
                style={{
                  background: e.blocked ? 'transparent' : pal.bg,
                  color: pal.fg,
                  borderColor: pal.fg,
                }}
                onClick={() => onToggleExperience(cell, e.experience_id, e.blocked)}
                title={e.blocked ? `${e.experience_name} — geblokkeerd, klik om te openen` : `${e.experience_name} — open, klik om te blokkeren`}
              >
                {e.blocked ? '✕' : '●'} {e.experience_name}
              </button>
            );
          })}
        </div>
        <div className="setup-override-cap">
          <button
            className="setup-override-btn"
            onClick={() => onAdjustCapacity(cell, -1)}
            aria-label="Capaciteit verlagen"
          >
            −
          </button>
          <span className="setup-override-cap-val">
            Max {effectiveCapacity}
          </span>
          <button
            className="setup-override-btn"
            onClick={() => onAdjustCapacity(cell, 1)}
            aria-label="Capaciteit verhogen"
          >
            +
          </button>
        </div>
        {reasonLine}
        <div className="setup-override-footer">
          <span className="muted setup-override-meta">
            {cell.totalBooked} / {effectiveCapacity}
            {cell.reducedToCapacity != null ? ' · cap aangepast' : ''}
            {anyExperienceBlocked ? ' · 1+ exp geblokkeerd' : ''}
          </span>
          {applyReason}
          <button
            className="setup-override-close-slot"
            onClick={() => onCloseRoom(cell)}
            title="Sluit het hele tijdslot voor alle experiences"
          >
            ✕ slot
          </button>
        </div>
      </div>
    </div>
  );
}

function reasonOrNull(r: string): string | null {
  const t = r.trim();
  return t ? t : null;
}
